import { createContext, useContext, useState, useEffect } from 'react';

// テナント情報を管理するContext
const TenantContext = createContext(null);

/**
 * サブドメインからテナントslugを抽出する
 * 
 * REACT_APP_BASE_DOMAIN を基準にサブドメイン部分を取得：
 *   tenant-a.example.com     (BASE_DOMAIN=example.com)     → "tenant-a"
 *   tenant-a.stg.example.com (BASE_DOMAIN=stg.example.com) → "tenant-a"
 *   example.com              (BASE_DOMAIN=example.com)     → null（ランディングページ表示）
 *   localhost                (BASE_DOMAIN未設定)            → 環境変数のデフォルト値を使用
 *   localhost                (BASE_DOMAIN=localhost)        → null（ランディングページ表示）
 */
const extractTenantSlug = () => {
    const hostname = window.location.hostname;
    const baseDomain = process.env.REACT_APP_BASE_DOMAIN;

    // ローカル開発環境（localhost）で REACT_APP_BASE_DOMAIN 未設定の場合はデフォルト値を使用
    if (!baseDomain && (hostname === 'localhost' || hostname === '127.0.0.1')) {
        return process.env.REACT_APP_DEFAULT_TENANT_SLUG || 'test-tenant';
    }

    // ベースドメインによるサブドメイン抽出

    // ルートドメインへの直接アクセス（サブドメインなし）
    if (baseDomain && hostname === baseDomain) {
        return null;
    }

    if (baseDomain && hostname.endsWith(`.${baseDomain}`)) {
        // "tenant-a.stg.example.com" - ".stg.example.com" → "tenant-a"
        const slug = hostname.slice(0, -(baseDomain.length + 1));
        if (slug && !slug.includes('.')) {
            return slug;
        }
    }

    // REACT_APP_BASE_DOMAIN 未設定時のフォールバック: 先頭パーツを使用
    const parts = hostname.split('.');
    if (parts.length >= 2) {
        return parts[0];
    }

    return process.env.REACT_APP_DEFAULT_TENANT_SLUG || 'default';
};

/**
 * TenantProvider - アプリ全体にテナント情報を提供
 */
export const TenantProvider = ({ children }) => {
    const [tenantSlug, setTenantSlug] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const slug = extractTenantSlug();
        setTenantSlug(slug);
        setIsLoading(false);
    }, []);

    const value = {
        tenantSlug,
        isLoading,
        isRootDomain: !isLoading && tenantSlug === null,
    };

    return (
        <TenantContext.Provider value={value}>
            {children}
        </TenantContext.Provider>
    );
};

/**
 * useTenant - テナント情報を取得するカスタムフック
 */
export const useTenant = () => {
    const context = useContext(TenantContext);
    if (!context) {
        throw new Error('useTenant must be used within a TenantProvider');
    }
    return context;
};

export default TenantContext;
